import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useState } from "react"
import { Button, Col, Modal, Row } from "react-bootstrap"
import { defineMessages, injectIntl } from "react-intl"
import { useQuery } from "react-query"
import GlobalHelper from "../../common/GlobalHelper"
import ActionButton from "../../common/button/actionbutton.component"
import MoneyInputForm from "../../common/form/moneyinput.component"
import Icon from "../../common/icon/icon.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import { SelectOption } from "../../common/types/select/types"
import CustomerSelect from "../../customer/selection.component.jsx"
import LocationSelect from "../../integration/LocationSelect"
import PaymentTypeSelect from "../../payment-type/paymenttypeselect.jsx"
import Contact from "../../state/contact/models/contact"
import ContactService from "../../state/contact/services/ContactService"
import CreditMemo from "../../state/credit-memo/CreditMemo"
import CreditMemoService, { CreditMemoPayload } from "../../state/credit-memo/CreditMemoService"
import IntlProps from "../../state/helpers/IntlProps"
import LocationService from "../../state/location/LocationService"

type Props = {
    contactId? : string
    onClose :  (id?: string | React.MouseEvent) => void
    canSelectContact? : boolean
}

const messages = defineMessages({
    "notes_placeholder": {
        id: "invoice.credit_memo_modal.notes_placeholder",
        defaultMessage: "Describe a reason that customer will see on their statements",
    },
    "internal_notes_placeholder": {
        id: "invoice.credit_memo_modal.internal_notes_placeholder",
        defaultMessage: "Enter an internal notes to describe the reason or the situation.",
    },
    "notes_label": {
        id:"invoice.credit_memo_modal.notes_label",
        defaultMessage:"Notes"
    },
    "internal_notes_label": {
        id:"invoice.credit_memo_modal.internal_notes_label",
        defaultMessage:"Internal notes"
    },
    "receive_payment": {
        id:"invoice.analytics.receive_payment_btn",
        defaultMessage:"Receive payment"
    },
    "contact": {
        id:"invoice.credit_memo_modal.contact_label",
        defaultMessage:"Contact"
    },
    "location": {
        id:"invoice.account_statement_modal.statement_location_label",
        defaultMessage:"Location"
    },
    "amount": {
        id:"invoice.credit_memo_modal.amount_label",
        defaultMessage:"Amount"
    },
    "payment_type": {
        id:"invoice.receive_payment.payment_type_label",
        defaultMessage:"Payment type"
    },
    "close": {
        id:"invoice.void_modal.close_btn",
        defaultMessage:"Close"
    }
})

const NewCreditMemoModal = (props: Props & IntlProps) => {

    const [selectedLocation, setSelectedLocation] = useState<SelectOption<string>>()
    const [contactId, setContactId] = useState(props.contactId || "")
    const [amount, setAmount] = useState(0)
    const [formValues, setFormValues] = useState({
        notes: "",
        internalNotes: "",
        currency: AppContext.getAccount().attributes.currency,
    })
    const [paymentTypeValues, setPaymentTypeValues] = useState({
        paymentType : "",
        customPaymentTypeId : ""
    })

    const handleInputChange = (event) => {
        setFormValues({
            ...formValues,
            [event.target.name]: event.target.value
        })
    }

    const { data : locations, isLoading : isLoadingLocations, isFetched} = useQuery(["getLocations"], async () => {
        return LocationService.fetchAllLocations(true)
    }, {
        onSuccess: (data) => {
            if (AppContext.getUserLoggedIn().attributes.locationId) {
                const location = data.find((loc) => loc.getId() === AppContext.getUserLoggedIn().attributes.locationId)
                if (location) {
                    setSelectedLocation( {
                        value : location.getId(),
                        label : location.getName()
                    }
                    )
                }
            }
        }
    })

    const { data : contact, isLoading : isLoadingContact} = useQuery(["getContact"], async () => {
        if (props.contactId) {
            const contact = await ContactService.GetPrimaryContactFromCompany(contactId)
            if (contact instanceof Contact) {
                return contact
            }
        }
    })

    const submit = async (e) => {
        e.preventDefault()
        const creditMemo = await CreditMemoService.post({
            notes: formValues.notes,
            internalNotes: formValues.internalNotes,
            amount: amount,
            currency: formValues.currency,
            contactId: contactId,
            deposit: {
                paymentType: paymentTypeValues.paymentType,
                paymentTypeId: paymentTypeValues.customPaymentTypeId,
            },
            locationId: selectedLocation?.value} as CreditMemoPayload
        )
        if(creditMemo instanceof CreditMemo) {
            props.onClose()
        }
    }

    const canSubmit = () => {
        return amount > 0 && paymentTypeValues.paymentType != ""
    }

    const handleSelectChange = (location: SelectOption<string> ) => {
        const selectedLocation = locations?.find(loc => loc.getId() === location.value)

        if (selectedLocation?.isDeleted() || !selectedLocation?.getEnabled()) {
            return
        }

        setSelectedLocation(location)
    }

    return (
        <Modal
            onHide={props.onClose}
            show={true}
            backdrop="static"
            size="lg">
            <Modal.Header>
                <Modal.Title>
                    <Icon value={"file-invoice-dollar"} className={"mr-2"} />
                    {props.intl.formatMessage(messages["receive_payment"])}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-4">
                    <Col xs={12} md={4} className="custom-form-label">
                        {props.intl.formatMessage(messages["contact"])}
                    </Col>
                    {isLoadingContact && (
                        <Col xs={12} md={8}>
                            <LoadingSpinner />
                        </Col>
                    )}
                    {!isLoadingContact && <Col xs={12} md={8}>
                        {(props.contactId || !props.canSelectContact) ? <span>{contact?.getName()}</span> :
                            <CustomerSelect
                                id={"contactId"}
                                name={"contactId"}
                                contactType={"persons"}
                                value={contactId}
                                onChange={(contactId) => {
                                    setContactId(contactId)
                                }}
                                clearable={false}
                            /> }
                    </Col>}
                </Row>
                <Row className="mb-4">
                    <Col xs={12} md={4} className="custom-form-label">
                        {props.intl.formatMessage(messages["location"])}
                    </Col>
                    {isLoadingLocations && (
                        <Col xs={12} md={8}>
                            <LoadingSpinner />
                        </Col>
                    )}
                    {!isLoadingLocations && isFetched &&
                    <Col xs={12} md={8}>
                        <LocationSelect
                            selectedLocationIds={selectedLocation}
                            isLoadingLocations={isLoadingLocations}
                            locations={locations}
                            multi={false}
                            onSelect={(location) => {
                                handleSelectChange(location)
                            }}
                        />
                    </Col>
                    }
                </Row>
                <Row className="mb-4">
                    <Col xs={12} md={4} className="custom-form-label">
                        {props.intl.formatMessage(messages["notes_label"])}
                    </Col>
                    <Col xs={12} md={8}>
                        <input
                            className="form-control"
                            id="notes"
                            name="notes"
                            onChange={handleInputChange}
                            type="text"
                            placeholder={props.intl.formatMessage(messages["notes_placeholder"])}
                        />
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col xs={12} md={4} className="custom-form-label">
                        {props.intl.formatMessage(messages["internal_notes_label"])}
                    </Col>
                    <Col xs={12} md={8}>
                        <textarea
                            className="form-control"
                            id="internalNotes"
                            name="internalNotes"
                            onChange={handleInputChange}
                            rows={2}
                            placeholder={props.intl.formatMessage(messages["internal_notes_placeholder"])}
                        />
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col xs={12} md={4} className="custom-form-label">
                        {props.intl.formatMessage(messages["amount"])}
                    </Col>
                    <Col xs={12} md={6}>
                        <MoneyInputForm
                            currency={formValues.currency}
                            id="amount"
                            name="amount"
                            value={amount}
                            onChange={(newValue) => {
                                setAmount(newValue)
                            }}
                        />
                    </Col>
                    <Col xs={12} md={2}>
                        <select
                            value={formValues.currency}
                            className="custom-select"
                            name="currency"
                            onChange={handleInputChange}
                        >
                            {GlobalHelper.getSupportedCurrenciesSelectOptions()}
                        </select>
                    </Col>
                </Row>
                <Row className="mb-4">
                    <Col xs={12} md={4} className="custom-form-label">
                        {props.intl.formatMessage(messages["payment_type"])}
                    </Col>
                    <Col xs={12} md={8}>
                        <PaymentTypeSelect
                            paymentType={paymentTypeValues.customPaymentTypeId ?? paymentTypeValues.paymentType}
                            onChange={(paymentType, customPaymentTypeId) =>
                                setPaymentTypeValues({
                                    paymentType : paymentType,
                                    customPaymentTypeId : customPaymentTypeId
                                })
                            }
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={props.onClose}
                    variant="secondary">
                    {props.intl.formatMessage(messages["close"])}
                </Button>
                <ActionButton
                    type="button"
                    className="btn btn-primary"
                    onClick={submit}
                    disabled={!canSubmit()}
                >
                    {props.intl.formatMessage(messages["receive_payment"])}
                </ActionButton>
            </Modal.Footer>
        </Modal>
    )
}

export default injectIntl(NewCreditMemoModal)


// WEBPACK FOOTER //
// ./src/invoice/credit-memo/NewCreditMemoModal.tsx